import * as _ from 'lodash';

import { AccountInfo, InviteData, TeamMembersData, UserData, TeamData } from '../types/teamMembers';
import { InviteDTO, TeamMembersDTO, UserDTO, TeamDTO, PendingOwnerDTO, InviteStatusType } from './server-types';

export const convertTeamMembersDTOToTeamMembersData = (teamMembersDTO: TeamMembersDTO): TeamMembersData => {
  const { users, invites, teams, pendingOwner } = teamMembersDTO;
  const shouldAddPendingOwner = pendingOwner && ![...users, ...invites].some(item => item.email === pendingOwner.email);
  return {
    accountInfo: convertAccountInfoDTOToAccountInfoData(teamMembersDTO),
    users: users.map(teamMemberDTO => convertUserDTOToUserData(teamMemberDTO, pendingOwner)),
    invites: convertInvitesDTOToInvitesData(invites, pendingOwner, shouldAddPendingOwner),
    teams: teams?.map(teamDTO => convertTeamDTOToTeamData(teamDTO)),
  };
};

export const validateTeamMembersDTO = (teamMembersDTO: TeamMembersDTO): TeamMembersDTO => {
  return Object.assign({}, teamMembersDTO, {
    users: teamMembersDTO.users.map(user => !_.isEmpty(user.roles) ? user : { ...user, roles: [] }),
    invites: teamMembersDTO.invites.map(invite => !_.isEmpty(invite.role) ? invite : { ...invite, role: '' }),
  });
};

const convertAccountInfoDTOToAccountInfoData = ({ accountInfo, targetAccountId, userId, permissions = [], loggedInAccountId, pendingOwner, contributorLimit }: TeamMembersDTO): AccountInfo => {
  return {
    image: prepareImage(accountInfo?.accountImage),
    name: accountInfo?.accountName,
    targetAccountId,
    userId,
    permissions: new Set(permissions),
    loggedInAccountId,
    transferOwnershipLink: pendingOwner?.acceptLink,
    contributorLimit: contributorLimit?.contributorLimit,
  };
};

export const convertUserDTOToUserData = (userDTO: UserDTO, pendingOwnerDTO?: PendingOwnerDTO): UserData => {
  const { email, profileImage, id, joinedTeamAt, name, policyIds } = userDTO;
  return ({
    email,
    id,
    joinedTeamAt: getDateFromStringDate(joinedTeamAt),
    name,
    profileImage: prepareImage(profileImage),
    roles: policyIds,
    isPendingOwner: getIsPendingOwner(email, pendingOwnerDTO),
  });
};

export const convertTeamDTOToTeamData = (teamDTO: TeamDTO): TeamData => {
  const { accountId, accountInfo, policyIds } = teamDTO;
  return ({
    id: accountId,
    accountName: accountInfo.accountName,
    accountImage: accountInfo.accountImage,
    roles: policyIds,
  });
};

export const convertInvitesDTOToInvitesData = (invitesDTO: InviteDTO[], pendingOwnerDTO?: PendingOwnerDTO, shouldAddPendingOwner?: boolean): InviteData[] => {
  const invitesData = invitesDTO.map(inviteDTO => convertInviteDTOToInviteData(inviteDTO, pendingOwnerDTO));
  if (shouldAddPendingOwner) {
    let dateCreated = new Date(pendingOwnerDTO.expirationDate);
    dateCreated.setDate(dateCreated.getDate() - 3);
    invitesData.push({
      id: '',
      roles: [],
      email: pendingOwnerDTO.email,
      status: InviteStatusType.PENDING,
      acceptLink: '',
      staffId: '',
      dateCreated,
      isPendingOwner: true,
    });
  }
  return invitesData;
};

export const convertInviteDTOToInviteData = (inviteDTO: InviteDTO, pendingOwnerDTO?: PendingOwnerDTO): InviteData => {
  const { id, policyIds, email, status, acceptLink, staffId, dateCreated } = inviteDTO;
  return {
    id,
    roles: policyIds,
    email,
    status,
    acceptLink,
    staffId,
    dateCreated: getDateFromStringDate(dateCreated),
    isPendingOwner: getIsPendingOwner(email, pendingOwnerDTO),
  };
};

const getDateFromStringDate = (stringDate: string): Date => {
  const date = stringDate && new Date(stringDate);
  if ((date instanceof Date) && (!isNaN(date.valueOf()))) {
    return date;
  } else {
    return null;
  }
};

export const prepareImage = (image: string) => {
  // Since we set a basename for our page history we must use absolute URL for the image
  if (image && !/^http/i.test(image)) {
    image = `https://${image}`;
  }

  // Checking whether the image is blank gravatar
  if (image && /(https?:\/\/(.+?\.)?gravatar\.com(\/[A-Za-z0-9\-\._~:\/\?#\[\]@!$&'\(\)\*\+,;\=]*)?)(\?|&)((d=blank)(&?))+/g.test(image)) {
    return null;
  }

  return image;
};

const getIsPendingOwner = (memberEmail: string, pendingOwnerDTO: PendingOwnerDTO): boolean =>
  pendingOwnerDTO?.email === memberEmail;
